import { useQuery } from "@tanstack/react-query";

import { useBulkQueryCache } from "#shared/hooks/useBulkQueryCache";

import { getPartFeaturesQueryData, setPartFeaturesQueryData } from "../infra/partFeatures.query";
import { partFeaturesBulkQuery } from "../infra/partFeaturesBulk.query";

/**
 * Optimized hook to fetch multiple part features in a single API call
 * instead of making N individual requests. This significantly reduces
 * network overhead and improves page load times.
 *
 * The hook is reactive to individual query updates. When a single part
 * features query is invalidated and refetched, the returned data automatically updates.
 */
export const usePartFeaturesBulk = (initialPartIds: string[], partIds: string[]) => {
  const {
    data,
    isInitialLoading: isBulkQueryInitialLoading,
    error,
  } = useQuery({
    ...partFeaturesBulkQuery({ partIds: initialPartIds }),
  });

  const { isInitialLoading } = useBulkQueryCache({
    isBulkLoading: isBulkQueryInitialLoading,
    bulkData: data,
    isBulkEnabled: initialPartIds.length > 0,
    initialIds: initialPartIds,
    currentIds: partIds,
    onPopulateCache: (bulkData, { queryClient, populatedKeys }) => {
      Object.entries(bulkData).forEach(([partId, features]) => {
        if (!populatedKeys.has(partId)) {
          const existingData = getPartFeaturesQueryData(queryClient, partId);

          if (!existingData) {
            setPartFeaturesQueryData(queryClient, partId, features);
          }

          populatedKeys.add(partId);
        }
      });
    },
  });

  return {
    isInitialLoading,
    error,
  };
};
